import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Android's hardware Back button, handled the way a native app behaves.
 *
 * Left to itself the WebView just walks back through history, which on the first
 * screen means leaving the app in a strange state - or landing on the login page
 * that the user has already passed through. Instead:
 *
 *   - on a top-level screen, Back minimises the app, exactly like a home button;
 *   - anywhere else, Back goes up one screen;
 *   - on the login screen it minimises too, so Back can never bounce a signed-in
 *     user backwards into signing in again.
 *
 * The Capacitor plugin is read off the runtime global rather than imported, so
 * the web build carries no native dependency and this is simply inert in a
 * browser.
 */
const ROOT_ROUTES = ['/dashboard', '/login', '/'];

export const AndroidBackButton = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const CapApp = window.Capacitor?.Plugins?.App;
    if (!CapApp?.addListener) return undefined;

    let remove;
    const handle = ({ canGoBack }) => {
      if (ROOT_ROUTES.includes(location.pathname)) {
        CapApp.minimizeApp?.();
        return;
      }
      if (canGoBack && window.history.length > 1) {
        navigate(-1);
      } else {
        navigate('/dashboard', { replace: true });
      }
    };

    const registration = CapApp.addListener('backButton', handle);
    // Capacitor 6 resolves a handle; older versions return it directly.
    Promise.resolve(registration).then((h) => {
      remove = h?.remove ? () => h.remove() : undefined;
    });

    return () => {
      if (remove) remove();
    };
  }, [navigate, location.pathname]);

  return null;
};

export default AndroidBackButton;
